/* eslint-disable react/no-array-index-key */
import {css, useTheme} from '@mui/material';
import {times} from 'lodash';
import React, {CSSProperties, PropsWithChildren, RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {GetStylesFunction, NumberUtils} from '../common';
import {getLocalStorage, useLocalStorage} from '../hooks/useLocalStorage';

export type LightSplitLayoutProps = {
  isVertical?: boolean;
  initSizes: number[];
  minSizes: number[];
  storageId: string;
  gutterSize?: number;
  hidden?: boolean[];
  noGrow?: boolean[];
  attributes?: Record<string, string | number | boolean | undefined>[];
  style?: CSSProperties;
  /**
   * One-shot default sizing of the first (filter) pane the first time the split is shown for this `storageId`
   * (no persisted size yet). `contentRef` must point at the flex-wrap filter-inputs row. If the filters fit in
   * `visibleRows` rows or fewer they are shown in full; otherwise the pane is sized to reveal 2 full rows plus
   * the fractional part of the next (default 2.5) so it's obvious more filter rows continue below. The user's
   * manual drag still wins on later visits. Vertical splits only.
   */
  autoFitFilterRows?: {
    /** Explicit ref to the flex-wrap filter-inputs row. Omit when the filters are rendered by a nested
     * component that instead marks its row with `data-filter-inputs` (found by querySelector in the pane). */
    contentRef?: RefObject<HTMLElement | null>;
    /** Rows to reveal; the fractional part becomes the "peek" of the next row. Defaults to 2.5. */
    visibleRows?: number;
    /** Extra px added below the content in the "fits fully" case (breathing room). Defaults to 16. */
    padding?: number;
    /**
     * Fit only while true (default true). Pass the "filters open" flag: measuring the collapsed panel would
     * store a wrong size. The fit runs once — the first time this is true with no persisted size.
     */
    enabled?: boolean;
  };
};
function getHasGutter(i: number, hidden?: boolean[]): boolean {
  for (let j = 0; j < i; j++) {
    if (!hidden?.[j]) return true;
  }
  return false;
}
function getPrevId(hidden: boolean[] | undefined, draggingId: number) {
  for (let i = draggingId - 1; i >= 0; i--) {
    if (!hidden?.[i]) return i;
  }
  return -1;
}
export function LightSplitLayout(props: PropsWithChildren<LightSplitLayoutProps>) {
  const {isVertical, initSizes, minSizes, storageId, gutterSize = 18, children, hidden, noGrow, attributes, autoFitFilterRows, ...extraProps} =
    props;
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const [sizes, _, setSizes] = useLocalStorage(storageId, initSizes);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Only auto-fit the very first time this split is shown (no persisted size yet). Captured once so a later
  // re-render (e.g. after the filters finish loading) can't retrigger it and stomp on a dragged size.
  const shouldAutoFit = useRef(!!autoFitFilterRows && getLocalStorage<number[] | null>(storageId, null) === null);
  // Set true on the user's first divider drag; blocks the settle fit below from stomping a manual size.
  const userDraggedRef = useRef(false);
  // Visual-only sizes for the pre-settle window (see below). Kept in a ref too so startDragging can commit
  // them without a stale closure.
  const [preSettleSizes, _setPreSettleSizes] = useState<number[] | null>(null);
  const preSettleSizesRef = useRef<number[] | null>(null);
  const setPreSettleSizes = useCallback((v: number[] | null) => {
    preSettleSizesRef.current = v;
    _setPreSettleSizes(v);
  }, []);
  // eslint-disable-next-line no-console
  const debugFit = (msg: string, extra?: Record<string, unknown>) =>
    typeof localStorage !== 'undefined' && localStorage.getItem('__DL_AUTOFIT_DEBUG') && console.log('[DL autoFit]', storageId, msg, extra ?? '');
  // TWO-PHASE fit. An IMMEDIATE pass right after mount sizes the pane visually (plain React state, NOT
  // persisted) so the very first paint is already about right. A SETTLE pass 700ms later re-measures — by
  // then the webfont swap and the filters' Collapse (500ms) have finished shifting the layout (~16px, which
  // made every single mount-time measurement come out a hair short) — and only THIS pass persists the size
  // and consumes the one-shot flag. Because the immediate pass never persists, a StrictMode double-invoke, a
  // remount during page load, or a not-yet-rendered filter row leaves the storage empty and simply retries
  // on the next effect run; the settle correction itself is a barely visible nudge.
  useEffect(() => {
    if (!shouldAutoFit.current) return;
    // Wait until the filter panel is in its measurable (expanded) state.
    if (autoFitFilterRows?.enabled === false) return;
    debugFit('scheduled');
    const computeFit = (label: string): number[] | null => {
      const bail = (msg: string) => {
        debugFit(`${label} bail: ${msg}`);
        return null;
      };
      const wrapper = wrapperRef.current;
      if (!wrapper) return bail('no wrapper');
      // The filter-inputs row is located either via an explicit `contentRef` or, for filters rendered by a
      // nested component, via a `[data-filter-inputs]` marker somewhere in the first pane.
      const content =
        autoFitFilterRows?.contentRef?.current ?? (wrapper.querySelector('[data-filter-inputs]') as HTMLElement | null);
      if (!content) return bail('no content');
      const contentChildren = Array.from(content.children) as HTMLElement[];
      if (!contentChildren.length) return bail('no children');
      const wrapperRect = wrapper.getBoundingClientRect();
      const wrapperTop = isVertical ? wrapperRect.top : wrapperRect.left;
      const total = isVertical ? wrapperRect.height : wrapperRect.width;
      if (total <= 0) return bail('zero total');
      // Filter fields are a flex-wrap row; group them by their (rounded) top edge to find the wrapped rows.
      const rowTops = Array.from(new Set(contentChildren.map((c) => Math.round(c.getBoundingClientRect().top)))).sort((a, b) => a - b);
      // Chrome BELOW the scrollable filter area (the card's bottom padding, e.g. 16px on report cards): the
      // scroll box fills `pane − chromeAbove − chromeBelow`, so the pane must include it or the box comes out
      // exactly that much short (scrollbar / deeper-than-wanted clipping). Measured from layout rects — the
      // settle pass (the one that persists) runs on settled DOM where this is reliable; the immediate pass may
      // still read 0 mid-transition, which is fine since it's visual-only and corrected at settle.
      let scrollEl: HTMLElement | null = content.parentElement;
      while (scrollEl && scrollEl !== wrapper && !['auto', 'scroll'].includes(window.getComputedStyle(scrollEl).overflowY)) {
        scrollEl = scrollEl.parentElement;
      }
      let chromeBelow = 0;
      if (isVertical && scrollEl && scrollEl !== wrapper) {
        let pane: HTMLElement | null = scrollEl;
        while (pane && pane.parentElement !== wrapper) pane = pane.parentElement;
        if (pane) chromeBelow = Math.max(pane.getBoundingClientRect().bottom - scrollEl.getBoundingClientRect().bottom, 0);
      }
      const visibleRows = autoFitFilterRows?.visibleRows ?? 2.5;
      const fullRows = Math.floor(visibleRows);
      let needed: number;
      if (rowTops.length <= fullRows) {
        // Fits within the target rows → show all of it (no peek), measured from the split's own top.
        needed = content.getBoundingClientRect().bottom - wrapperTop + chromeBelow + (autoFitFilterRows?.padding ?? 16);
      } else {
        // Peek: reveal `fullRows` full rows + a fraction of the next FIELD, so it's obvious the filters
        // continue below. `rowTops[fullRows]` is the top of the first hidden row; the fraction applies to the
        // field's own height (NOT field+row-gap, which over-revealed — a "half" peek showed ~the whole field).
        const fieldHeight = contentChildren[0].getBoundingClientRect().height || rowTops[1] - rowTops[0];
        needed = rowTops[fullRows] + (visibleRows - fullRows) * fieldHeight - wrapperTop + chromeBelow;
      }
      // Distribute across N panes: pin the first (filter) pane to `needed`, scale the rest to fill the remainder
      // keeping their relative proportions. `sizes` are relative flex units, so working in px is fine here.
      const firstMin = minSizes?.[0] ?? 0;
      const restMin = (minSizes ?? []).slice(1).reduce((acc, m) => acc + (m ?? 0), 0) + gutterSize;
      const first = NumberUtils.clamp(needed, firstMin, Math.max(total - restMin, firstMin));
      const remaining = Math.max(total - first, 0);
      const rest = (sizes ?? initSizes).slice(1);
      const restTotal = rest.reduce((acc, s) => acc + (s ?? 0), 0);
      const newRest =
        restTotal > 0 ? rest.map((s) => ((s ?? 0) / restTotal) * remaining) : rest.map(() => remaining / Math.max(rest.length, 1));
      debugFit(`${label} fit OK`, {rows: rowTops.length, needed: Math.round(needed), total: Math.round(total)});
      return [first, ...newRest];
    };
    // Immediate pass — visual only.
    const immediate = computeFit('immediate');
    if (immediate) setPreSettleSizes(immediate);
    const settleTimer = setTimeout(() => {
      debugFit('settle fired', {userDragged: userDraggedRef.current});
      setPreSettleSizes(null);
      if (userDraggedRef.current) {
        shouldAutoFit.current = false; // the user already sized it manually — never auto-fit again
        return;
      }
      const settled = computeFit('settle');
      if (settled) {
        setSizes(settled);
        shouldAutoFit.current = false;
      }
      // on a failed fit the flag stays set, so the next effect run (remount / enabled flip) retries
    }, 700);
    // The persisted sizes are flex RATIOS, so if the wrapper's height changes after the fit — report tables
    // finish loading and grow the (min-height'd) split well past its fit-time height — the filter pane grows
    // proportionally with it, leaving a big empty band under the filters. Track the wrapper for the rest of
    // this first visit and re-fit on any real size change, until the user drags (their size then wins).
    let resizeDebounce: ReturnType<typeof setTimeout> | undefined;
    let ro: ResizeObserver | undefined;
    const wrapperEl = wrapperRef.current;
    if (typeof ResizeObserver !== 'undefined' && wrapperEl) {
      let lastTotal = isVertical ? wrapperEl.getBoundingClientRect().height : wrapperEl.getBoundingClientRect().width;
      ro = new ResizeObserver(() => {
        const w = wrapperRef.current;
        if (!w) return;
        if (userDraggedRef.current) {
          ro?.disconnect();
          return;
        }
        const t = isVertical ? w.getBoundingClientRect().height : w.getBoundingClientRect().width;
        if (Math.abs(t - lastTotal) < 2) return;
        lastTotal = t;
        clearTimeout(resizeDebounce);
        resizeDebounce = setTimeout(() => {
          if (userDraggedRef.current) return;
          debugFit('wrapper resized → re-fit', {total: Math.round(t)});
          const refitted = computeFit('resize');
          if (refitted) {
            setSizes(refitted);
            shouldAutoFit.current = false;
          }
        }, 200);
      });
      ro.observe(wrapperEl);
    }
    return () => {
      debugFit('cleanup (timer cleared)');
      clearTimeout(settleTimer);
      clearTimeout(resizeDebounce);
      ro?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFitFilterRows?.enabled]);
  const gutterClass = isVertical ? 'gutter gutter-vertical' : 'gutter gutter-horizontal';
  const childrenArray = React.Children.toArray(children);
  const splitWrapperRefs = useMemo(() => {
    return times(childrenArray.length, () => React.createRef<HTMLDivElement | null>());
  }, [childrenArray.length]);
  const [draggingGutterId, setDraggingGutterId] = useState(-1);
  const startDragging = useCallback(
    (target: HTMLElement | null) => {
      const gutterId = +(target?.getAttribute('data-gutterid') ?? -1);
      if (gutterId !== -1) {
        userDraggedRef.current = true; // cancels the autoFit settle fit
        // Commit the visual pre-settle size so the drag continues from what's on screen.
        if (preSettleSizesRef.current) {
          setSizes(preSettleSizesRef.current);
          setPreSettleSizes(null);
        }
      }
      setDraggingGutterId(gutterId);
    },
    [setSizes, setPreSettleSizes]
  );
  const endDragging = useCallback(() => {
    setDraggingGutterId(-1);
  }, []);
  const preventDefaultDrag = useCallback((event) => {
    event.preventDefault();
  }, []);
  const onDrag = useCallback(
    (pos: number) => {
      const prevId = getPrevId(hidden, draggingGutterId);
      const gutterId = draggingGutterId;
      if (gutterId === -1 || prevId === -1) return;
      // move sizes to mouse pos
      const firstRef = splitWrapperRefs[prevId].current;
      const secondRef = splitWrapperRefs[gutterId].current;
      if (pos === 0) return;
      let startPos;
      let endPos;
      if (isVertical) {
        startPos = firstRef?.getBoundingClientRect()?.top;
        endPos = secondRef?.getBoundingClientRect()?.bottom;
      } else {
        startPos = firstRef?.getBoundingClientRect()?.left;
        endPos = secondRef?.getBoundingClientRect()?.right;
      }
      if (!startPos || !endPos) return;
      let t = NumberUtils.getLerpTime(pos, startPos + gutterSize / 2, endPos - gutterSize / 2);
      // clamp to minSizes
      const maxPosDiff = endPos - startPos;
      const firstMinSize = (minSizes?.[prevId] ?? 0) + gutterSize; // NOTE: both can have gutters
      const firstMinSizeMinT = firstMinSize / maxPosDiff;
      const secondMinSize = (minSizes?.[gutterId] ?? 0) + gutterSize;
      const secondMinSizeMinT = secondMinSize / maxPosDiff;
      t = NumberUtils.clamp(t, firstMinSizeMinT, 1 - secondMinSizeMinT);
      // set newSizes
      const newSizes = [...sizes];
      const sizeToTrade = (newSizes?.[prevId] ?? 0) + (newSizes?.[gutterId] ?? 0);
      newSizes[prevId] = t * sizeToTrade;
      newSizes[gutterId] = (1 - t) * sizeToTrade;
      setSizes(newSizes);
    },
    [isVertical, hidden, draggingGutterId, sizes, minSizes, gutterSize, setSizes, splitWrapperRefs]
  );
  const isDragging = draggingGutterId !== -1;
  return (
    <div
      {...extraProps}
      ref={wrapperRef}
      css={isVertical ? muiStyles.splitWrapperVertical : muiStyles.splitWrapperHorizontal}
      onMouseUp={endDragging}
      onTouchEnd={endDragging}
      onMouseMove={(event) => onDrag(isVertical ? event.clientY : event.clientX)}
      onTouchMove={(event) => {
        onDrag(isVertical ? event.touches[0].clientY : event.touches[0].clientX);
      }}
    >
      {childrenArray.map((v, i) => {
        // preSettleSizes = the immediate autoFit's visual-only sizing for the pre-settle window (see above).
        const size = (preSettleSizes ?? sizes)?.[i] ?? 50;
        const hasGutter = getHasGutter(i, hidden);
        const minSize = (minSizes?.[i] ?? 0) + (hasGutter ? gutterSize : 0);
        const minWidth = isVertical ? {} : {minWidth: minSize};
        const minHeight = isVertical ? {minHeight: minSize} : {};
        const visible = hidden?.[i] === undefined || hidden?.[i] === false;
        const cantGrow = noGrow?.[i] ?? false;
        const cantResize = (noGrow?.[i - 1] ?? false) || cantGrow;
        const gutterSizeString = `${gutterSize}px`;
        return (
          <div
            ref={splitWrapperRefs[i] as RefObject<HTMLDivElement>}
            key={i}
            style={{
              ...minWidth,
              ...minHeight,
              flex: cantGrow ? '0 0 auto' : `${size} 0 ${hasGutter ? gutterSizeString : 0}`,
              display: visible ? 'flex' : 'none',
              flexDirection: isVertical ? 'column' : 'row',
            }}
          >
            {hasGutter && (
              <div
                key={`gutter-${i}`}
                data-gutterid={i}
                style={{
                  flex: `0 0 ${gutterSize}px`,
                  cursor: isDragging ? 'default' : undefined,
                }}
                {...(cantResize
                  ? {}
                  : {
                      className: gutterClass,
                      onDragStart: preventDefaultDrag,
                      onMouseDown: (event) => startDragging(event.target as HTMLElement | null),
                      onTouchStart: (event) => startDragging(event.touches[0].target as HTMLElement | null),
                    })}
              />
            )}
            <div
              {...(attributes?.[i] ?? {})}
              key={`splitContent-${i}`}
              css={isVertical ? muiStyles.splitContentVertical : muiStyles.splitContentHorizontal}
              style={{
                userSelect: isDragging ? 'none' : undefined,
                pointerEvents: isDragging ? 'none' : undefined,
              }}
            >
              {v}
            </div>
          </div>
        );
      })}
    </div>
  );
}
const getStyles: GetStylesFunction = (options) => ({
  splitWrapperHorizontal: css`
    width: 100%;
    height: 100%;
    display: flex;
  `,
  splitWrapperVertical: css`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  `,
  splitContentHorizontal: css`
    overflow-x: auto;
    flex: 1;
    height: 100%;
    & > * {
      width: 100%;
      height: 100%;
    }
  `,
  splitContentVertical: css`
    overflow-y: auto;
    width: 100%;
    flex: 1;
    & > * {
      width: 100%;
      height: 100%;
    }
  `,
});
